import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AppSidebar } from "../components/app-sidebar";
import { SiteHeader } from "../components/site-header";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { cn } from "../lib/utils";
import { useWorkflows, useWorkflowAudits, Workflow, WorkflowAudit } from "../lib/api";
import { Loader } from "../components/ui/loader";
import { useToast } from "../lib/use-toast";

/**
 * Helper function to get badge styling based on processing result
 */
const getStatusBadge = (processingResult: string) => {
  switch (processingResult) {
    case 'PROCESSED':
      return {
        variant: 'outline' as const,
        className: 'text-xs py-0 h-5 bg-green-100 text-green-800 hover:bg-green-100 border-green-200',
        label: 'SUCCESS'
      };
    case 'SKIPPED':
      return {
        variant: 'outline' as const, 
        className: 'text-xs py-0 h-5 bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200',
        label: 'SKIPPED'
      };
    case 'FAILED':
      return {
        variant: 'outline' as const,
        className: 'text-xs py-0 h-5 bg-red-100 text-red-800 hover:bg-red-100 border-red-200', 
        label: 'FAILURE'
      };
    default:
      return {
        variant: 'outline' as const,
        className: 'text-xs py-0 h-5 bg-gray-100 text-gray-800 hover:bg-gray-100 border-gray-200',
        label: processingResult
      };
  }
};

/**
 * Deduplicate audit records by sentence, keeping one representative per unique sentence.
 */
const deduplicateAuditsBySentence = (audits: WorkflowAudit[]): WorkflowAudit[] => {
  const seen = new Set<string>();
  return audits.filter((audit) => {
    const key = audit.sentence ?? `${audit.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * LogsPage component
 * 
 * Displays migration logs with paginated accordions
 */
const LogsPage: React.FC = () => {
  const { toast } = useToast();
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  
  // Expanded accordion state
  const [expandedWorkflow, setExpandedWorkflow] = useState<number | null>(null);
  
  // Fetch workflows
  const { 
    data: workflowsData, 
    isLoading: isLoadingWorkflows, 
    error: workflowsError 
  } = useWorkflows(page, size, 'createdAt', 'Desc');
  
  // Fetch audit records for expanded workflow
  const {
    data: auditsData,
    isLoading: isLoadingAudits
  } = useWorkflowAudits(
    expandedWorkflow,
    0,
    100,
    'id',
    'desc'
  );
  
  // Handle accordion value change
  const handleAccordionChange = (value: string) => {
    // If value is empty, the accordion is being closed
    if (!value) {
      setExpandedWorkflow(null);
      return;
    }
    
    // Extract workflow ID from the value (format: "workflow-{id}")
    const workflowId = parseInt(value.replace('workflow-', ''), 10);
    setExpandedWorkflow(workflowId);
  };
  
  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setExpandedWorkflow(null); // Close any open accordion when changing page
  };
  
  // Show error toast if there's an error
  React.useEffect(() => {
    if (workflowsError) {
      toast({
        variant: "destructive",
        title: "Error loading workflows",
        description: "Failed to load workflow data. Please try again later."
      });
    }
  }, [workflowsError, toast]);
  
  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "16rem",
        "--header-height": "3rem",
      } as React.CSSProperties}
      className="min-h-screen"
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="pt-0">
        <SiteHeader />
        <div className="flex flex-1 flex-col p-4">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">Migration Logs</h1>
            <p className="text-muted-foreground mt-1">
              View migration logs and workflow history
            </p>
          </div>
          <div>
              {isLoadingWorkflows ? (
                <div className="flex justify-center items-center py-12">
                  <Loader className="w-8 h-8" />
                </div>
              ) : !workflowsData || workflowsData.data.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No workflow logs found.
                </div>
              ) : (
                <>
                  {/* Accordions */}
                  <div className="space-y-2">
                    <Accordion 
                      type="single" 
                      collapsible 
                      onValueChange={handleAccordionChange}
                    >
                      {workflowsData.data.map((workflow) => {
                        const hasAudits = (workflow.auditCount ?? 0) > 0;
                        return (
                        <AccordionItem
                          key={workflow.id}
                          value={`workflow-${workflow.id}`}
                          className="w-full border rounded-md mb-2 px-3"
                        >
                          <AccordionTrigger
                            disabled={!hasAudits}
                            className={cn("py-4", !hasAudits && "cursor-default [&>svg]:hidden")}
                          >
                            <div className="flex flex-col items-start text-left w-full">
                              <div className="flex items-center gap-2 mb-1 w-full overflow-hidden">
                                <Badge
                                  variant="outline"
                                  className="font-mono text-xs bg-slate-100 flex-shrink-0 min-w-fit whitespace-nowrap"
                                >
                                  {workflow.code}
                                </Badge>
                                <h3 className="font-semibold min-w-0 flex-1">
                                  {workflow.name}
                                </h3>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {workflow.description || 'No description provided'} •
                                {workflow.executionData?.facilityName && ` ${workflow.executionData.facilityName} • `}
                                {workflow.executionData?.databaseName && `${workflow.executionData.databaseName} • `}
                                {workflow.executionData?.checklistNames && workflow.executionData.checklistNames.length > 0 && `${workflow.executionData.checklistNames.join(', ')} • `}
                                {new Date(workflow.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            {expandedWorkflow === workflow.id && (
                              <>
                                {isLoadingAudits ? (
                                  <div className="flex justify-center items-center py-2">
                                    <Loader className="w-5 h-5" />
                                  </div>
                                ) : !auditsData || auditsData.data.length === 0 ? (
                                  <div className="text-center py-2 text-xs text-muted-foreground">
                                    No audit records found
                                  </div>
                                ) : (
                                  <div className="space-y-1 pl-3 border-l border-muted pt-1 pb-2">
                                    {deduplicateAuditsBySentence(auditsData.data).map((audit) => {
                                      const badge = getStatusBadge(audit.processingResult);
                                      return (
                                        <div key={audit.id} className="flex items-start gap-1.5">
                                          <Badge
                                            variant={badge.variant}
                                            className={badge.className}
                                          >
                                            {badge.label}
                                          </Badge>
                                          <span className="text-xs font-mono whitespace-pre-wrap">
                                            {audit.sentence ?? '—'}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </div>

                  {/* Pagination Controls - Bottom */}
                  {workflowsData.totalPages > 0 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-xs text-muted-foreground">
                        Page {workflowsData.page + 1} of {workflowsData.totalPages}
                      </div>
                      <div className="flex items-center space-x-1">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 px-2"
                          disabled={workflowsData.first}
                          onClick={() => handlePageChange(page - 1)}
                        >
                          <ChevronLeft className="h-3 w-3" />
                        </Button>
                        
                        {Array.from({ length: Math.min(workflowsData.totalPages, 3) }, (_, i) => {
                          // Show pages around the current page
                          let pageNum = i;
                          if (workflowsData.totalPages > 3) {
                            if (page < 2) {
                              pageNum = i;
                            } else if (page >= workflowsData.totalPages - 2) {
                              pageNum = workflowsData.totalPages - 3 + i;
                            } else {
                              pageNum = page - 1 + i;
                            }
                          }
                          
                          return (
                            <Button 
                              key={pageNum} 
                              variant="outline" 
                              size="sm"
                              className={cn(
                                "h-7 w-7 p-0",
                                pageNum === page ? 'bg-primary text-primary-foreground' : ''
                              )}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum + 1}
                            </Button>
                          );
                        })}
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="h-7 px-2"
                          disabled={workflowsData.last}
                          onClick={() => handlePageChange(page + 1)}
                        >
                          <ChevronRight className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default LogsPage;
